import type { Ref } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import type { GuideCardData } from '../types';
import { GuideCardFront } from './card/GuideCardFront';
import { CARD_EXPORT_WIDTH_PX, CARD_HEIGHT_MM, CARD_WIDTH_MM } from './card/cardTokens';

export const PRINT_ROOT_CLASS = 'trplin-guide-card-print-root';

/**
 * Mounted only while a printable card is on the page, so the rest of the app
 * keeps its normal print behaviour. The page box is exactly one card with no
 * margins (which also suppresses browser headers/footers), and everything in
 * <body> except the print root is removed from the print layout.
 */
export const GUIDE_CARD_PRINT_CSS = `
@page { size: ${CARD_WIDTH_MM}mm ${CARD_HEIGHT_MM}mm; margin: 0; }
@media print {
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    width: ${CARD_WIDTH_MM}mm !important;
    height: ${CARD_HEIGHT_MM}mm !important;
    min-width: 0 !important;
    min-height: 0 !important;
    overflow: hidden !important;
    background: #fff !important;
  }
  body > *:not(.${PRINT_ROOT_CLASS}) { display: none !important; }
  .${PRINT_ROOT_CLASS} {
    position: static !important;
    left: auto !important;
    width: ${CARD_WIDTH_MM}mm !important;
    height: ${CARD_HEIGHT_MM}mm !important;
    overflow: hidden !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .${PRINT_ROOT_CLASS}, .${PRINT_ROOT_CLASS} * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
`;

interface PrintableGuideCardProps {
  card: GuideCardData;
  /** Pre-fetched data URLs so captures never wait on (or fail) a network request. */
  photoSrc: string | null;
  logoSrc: string | null;
  cardRef: Ref<HTMLDivElement>;
}

/**
 * Export/print source: only the card artwork, at a fixed 856px width, kept
 * off-screen. PNG/PDF capture this node directly; print shows only this node.
 */
export function PrintableGuideCard({ card, photoSrc, logoSrc, cardRef }: PrintableGuideCardProps) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <Box
      className={PRINT_ROOT_CLASS}
      aria-hidden
      data-testid="guide-card-print-root"
      sx={{
        position: 'fixed',
        top: 0,
        left: '-10000px',
        width: `${CARD_EXPORT_WIDTH_PX}px`,
        pointerEvents: 'none',
      }}
    >
      <GlobalStyles styles={GUIDE_CARD_PRINT_CSS} />
      <GuideCardFront
        card={card}
        surface="export"
        photoSrc={photoSrc}
        logoSrc={logoSrc}
        cardRef={cardRef}
      />
    </Box>,
    document.body
  );
}
